import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Reorder, useDragControls } from "framer-motion";
import Cropper from 'react-easy-crop'; // <- Nueva librería para el cuadro de recorte
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@config/firebaseConfig";
import { uploadImage } from '@services/adminCloudinaryService';
import { getNewProductRef, saveProductWithRef } from '@services/adminCreateProductFirebaseService';
import styles from './AdminProductEdit.module.css';
import IconPlaceholder from '@/ui/assets/icons/ImageCreateReceta.svg';
import man from '@icons/man-svgrepo-com.svg';
import { Categories, POPULAR_CATEGORY_ID } from '@/data/categories.js';
import CategoryButton from '@/ui/components/client/CategoryButton/CategoryButton';

const MAX_IMAGES = 10;
const MAX_FILE_SIZE_MB = 5;

// ==========================================
// SUBCOMPONENTE: MODAL CON CUADRO DE RECORTE Y OPTIMIZACIÓN
// ==========================================
const OptimizationModal = ({ isOpen, file, onClose, onConfirm }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const [resolution, setResolution] = useState('1080');
    const [optimize, setOptimize] = useState(true);

    // Cargar la imagen en el cropper al abrir el modal
    useEffect(() => {
        if (file) {
            const reader = new FileReader();
            reader.addEventListener('load', () => setImageSrc(reader.result));
            reader.readAsDataURL(file);
        } else {
            setImageSrc(null);
        }
    }, [file]);

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    if (!isOpen || !file) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', justifyContent: 'center',
            alignItems: 'center', zIndex: 2000, padding: '20px'
        }}>
            <div style={{
                backgroundColor: '#fff', padding: '24px', borderRadius: '12px',
                maxWidth: '500px', width: '100%', color: '#333', boxShadow: '0 4px 20px rgba(0,0,0,0.25)'
            }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    Ajustar y Optimizar Imagen
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px' }}>
                    Arrastra la imagen dentro del cuadro para encuadrar tu producto.
                </p>

                {/* CONTENEDOR DEL CUADRO DE RECORTE (Relación 1:1 / Cuadrado para e-commerce) */}
                <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '280px',
                    backgroundColor: '#333',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginBottom: '15px'
                }}>
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1} // Puedes cambiar a 4/3 o 16/9 si lo prefieres
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    )}
                </div>

                {/* Control de Zoom */}
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#555' }}>Zoom:</span>
                    <input
                        type="range"
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        aria-label="Zoom"
                        onChange={(e) => setZoom(Number(e.target.value))}
                        style={{ flex: 1 }}
                    />
                </div>

                {/* Opciones de Tamaño de Salida */}
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontWeight: '600', display: 'block', marginBottom: '6px', fontSize: '0.85rem' }}>
                        Resolución de guardado:
                    </label>
                    <select
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.9rem' }}
                    >
                        <option value="1080">Full HD Cuadrado (1080px × 1080px)</option>
                        <option value="720">Estándar Web Ligero (720px × 720px)</option>
                        <option value="450">Miniatura / Catálogo (450px × 450px)</option>
                    </select>
                </div>

                {/* Checkbox de Compresión */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                        type="checkbox"
                        id="optimizeCheck"
                        checked={optimize}
                        onChange={(e) => setOptimize(e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <label htmlFor="optimizeCheck" style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}>
                        <strong>Compresión inteligente:</strong> Reduce el peso del archivo (.jpg) para cargas rápidas.
                    </label>
                </div>

                {/* Botones */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#eee', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm({ resolution, optimize, croppedAreaPixels, imageSrc })}
                        style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#007bff', color: '#fff', fontWeight: '500', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                        Recortar y Guardar
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// FUNCIÓN AUXILIAR: PROCESAMIENTO Y RECORTE CON CANVAS
// ==========================================
const getCroppedAndOptimizedImg = (imageSrc, pixelCrop, resolution, optimize, fileName) => {
    return new Promise((resolve) => {
        const image = new Image();
        image.src = imageSrc;
        image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Definimos el tamaño del lienzo final según la resolución seleccionada
            const targetSize = parseInt(resolution, 10);
            canvas.width = targetSize;
            canvas.height = targetSize;

            // Dibujamos únicamente el fragmento seleccionado del cuadro de recorte (pixelCrop) al tamaño de destino
            ctx.drawImage(
                image,
                pixelCrop.x,
                pixelCrop.y,
                pixelCrop.width,
                pixelCrop.height,
                0,
                0,
                targetSize,
                targetSize
            );

            const outputType = optimize ? 'image/jpeg' : 'image/png';
            const quality = optimize ? 0.85 : 1.0;

            canvas.toBlob((blob) => {
                const finalName = fileName.replace(/\.[^/.]+$/, "") + (optimize ? ".jpg" : ".png");
                const newFile = new File([blob], finalName, {
                    type: outputType,
                    lastModified: Date.now(),
                });
                resolve(newFile);
            }, outputType, quality);
        };
    });
};

// ==========================================
// COMPONENTE: PREVIEW ITEM
// ==========================================
const PreviewItem = ({ item, index, selectedImageIdToReplace, onSelect, onRemove, loading }) => {
    const controls = useDragControls();
    const isPointerDown = useRef(false);
    const dragStarted = useRef(false);

    const handlePointerDown = (event) => {
        event.stopPropagation();
        isPointerDown.current = true;
        dragStarted.current = false;
    };

    const handlePointerMove = (event) => {
        if (!isPointerDown.current || dragStarted.current) return;
        dragStarted.current = true;
        controls.start(event);
    };

    const handlePointerUp = () => {
        isPointerDown.current = false;
        dragStarted.current = false;
    };

    return (
        <Reorder.Item
            value={item}
            dragListener={false}
            dragControls={controls}
            whileDrag={{ scale: 1.1, zIndex: 100, rotate: 2 }}
            className={`${styles.previewItem} ${selectedImageIdToReplace === item.id ? styles.selectedForReplace : ''}`}
            onClick={() => onSelect(item.id)}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onDragEnd={handlePointerUp}
        >
            <img
                src={item.previewUrl}
                alt={`Vista previa ${index + 1}`}
                className={styles.previewImage}
                onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/150?text=Error+Image';
                }}
            />
            {index === 0 && <span className={styles.coverBadge}>Portada</span>}
            <button
                type="button"
                className={styles.removePreviewBtn}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.id);
                }}
                disabled={loading}
                aria-label="Quitar imagen"
            >
                ×
            </button>
        </Reorder.Item>
    );
};

// ==========================================
// COMPONENTE PRINCIPAL: ADMIN PRODUCT EDIT
// ==========================================
function AdminProductEdit() {
    const navigate = useNavigate();
    const location = useLocation();

    const availableCategories = Categories.filter((category) => category.id !== POPULAR_CATEGORY_ID);
    const initialData = location.state || {};
    const isEditMode = !!(initialData.id || initialData._id);

    const formatNumber = (num) => {
        if (!num) return "";
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    const existingUrls = initialData.image_urls || initialData.images_list ||
        (initialData.image_url ? [initialData.image_url] : []);

    const defaultCategory = availableCategories.find(category => category.id === initialData.category)?.id ||
        availableCategories[0]?.id || '';

    const [form, setForm] = useState({
        id: initialData.id || initialData._id || '',
        name: initialData.name || '',
        price: initialData.price || 0,
        description: initialData.description || '',
        category: initialData.category || defaultCategory,
        image_url: initialData.image_url || '',
        image_urls: existingUrls,
        is_active: initialData.is_active ?? true,
    });

    const [displayPrice, setDisplayPrice] = useState(formatNumber(initialData.price || ''));
    const [activeCategory, setActiveCategory] = useState(form.category || defaultCategory);
    const [loading, setLoading] = useState(false);
    const [selectedImageIdToReplace, setSelectedImageIdToReplace] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState(() => {
        if (existingUrls && existingUrls.length > 0) {
            return existingUrls.map((url, index) => ({
                id: `remote-${index}-${Date.now()}`,
                file: null,
                previewUrl: url,
                isRemote: true
            }));
        }
        return [];
    });

    // ESTADOS DE LA COLA MULTI-RECORTE
    const [pendingFiles, setPendingFiles] = useState([]);
    const [currentFileToOptimize, setCurrentFileToOptimize] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const selectedFilesRef = useRef(selectedFiles);
    selectedFilesRef.current = selectedFiles;

    useEffect(() => {
        return () => {
            selectedFilesRef.current.forEach((item) => {
                if (!item.isRemote && item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            });
        };
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "price") {
            const rawValue = value.replace(/\./g, "");
            if (!/^\d*$/.test(rawValue)) return;
            setForm(prev => ({ ...prev, price: Number(rawValue) }));
            setDisplayPrice(formatNumber(rawValue));
        } else {
            const finalValue = type === "checkbox" ? checked : value;
            setForm(prev => ({ ...prev, [name]: finalValue }));
        }
    };

    const handleFileChange = (e) => {
        const incoming = Array.from(e.target.files || []);
        if (!incoming.length) return;

        setPendingFiles(incoming);
        setCurrentFileToOptimize(incoming[0]);
        setIsModalOpen(true);

        e.target.value = '';
    };

    // Ejecuta el recorte real usando las coordenadas del recuadro visual
    const handleConfirmOptimization = async ({ resolution, optimize, croppedAreaPixels, imageSrc }) => {
        if (!currentFileToOptimize || !croppedAreaPixels) return;
        setIsModalOpen(false);

        const processedFile = await getCroppedAndOptimizedImg(
            imageSrc,
            croppedAreaPixels,
            resolution,
            optimize,
            currentFileToOptimize.name
        );

        const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
        if (processedFile.size > maxBytes) {
            alert(`Incluso tras recortar, la imagen supera el límite de ${MAX_FILE_SIZE_MB}MB.`);
            moveToNextFile();
            return;
        }

        const newPhotoObject = {
            id: `local-${processedFile.name}-${processedFile.size}-${Date.now()}-${Math.random()}`,
            file: processedFile,
            previewUrl: URL.createObjectURL(processedFile),
            isRemote: false
        };

        if (selectedImageIdToReplace) {
            setSelectedFiles((prev) => {
                const updated = [...prev];
                const indexToReplace = updated.findIndex((item) => item.id === selectedImageIdToReplace);

                if (indexToReplace !== -1) {
                    const itemToReplace = updated[indexToReplace];
                    if (!itemToReplace.isRemote && itemToReplace.previewUrl) {
                        URL.revokeObjectURL(itemToReplace.previewUrl);
                    }
                    updated[indexToReplace] = newPhotoObject;
                }
                return updated.slice(0, MAX_IMAGES);
            });
            setSelectedImageIdToReplace(null);
        } else {
            setSelectedFiles((prev) => {
                const combined = [...prev, newPhotoObject];
                if (combined.length > MAX_IMAGES) {
                    alert(`Máximo ${MAX_IMAGES} imágenes por producto.`);
                    URL.revokeObjectURL(newPhotoObject.previewUrl);
                    return prev;
                }
                return combined;
            });
        }

        moveToNextFile();
    };

    const moveToNextFile = () => {
        setPendingFiles((prev) => {
            const nextQueue = prev.slice(1);
            if (nextQueue.length > 0) {
                setCurrentFileToOptimize(nextQueue[0]);
                setIsModalOpen(true);
            } else {
                setCurrentFileToOptimize(null);
            }
            return nextQueue;
        });
    };

    const removeFile = (id) => {
        setSelectedFiles((prev) => {
            const item = prev.find((f) => f.id === id);
            if (!item?.isRemote && item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
            return prev.filter((f) => f.id !== id);
        });
        if (selectedImageIdToReplace === id) setSelectedImageIdToReplace(null);
    };

    const handleImageSelect = (id) => {
        setSelectedImageIdToReplace((current) => current === id ? null : id);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.price || selectedFiles.length === 0) {
            alert("Completa nombre, precio y al menos una imagen.");
            return;
        }

        setLoading(true);
        try {
            let productId = form.id;
            let productRef = null;

            if (isEditMode) {
                if (!productId) {
                    alert("Error: No se encontró el ID del producto para editar.");
                    setLoading(false);
                    return;
                }
                productRef = doc(db, "products", productId);
            } else {
                productRef = getNewProductRef();
                productId = productRef.id;
            }

            console.log("Subiendo imágenes a Cloudinary...");
            const finalImageUrls = await Promise.all(
                selectedFiles.map(async (item, index) => {
                    if (item.isRemote) {
                        return item.previewUrl;
                    } else {
                        return await uploadImage(item.file, productId, index);
                    }
                })
            );

            const finalFields = {
                name: form.name,
                price: Number(form.price),
                description: form.description,
                category: form.category,
                image_url: finalImageUrls[0] || "",
                image_urls: finalImageUrls,
                is_active: form.is_active,
            };

            if (isEditMode) {
                await updateDoc(productRef, finalFields);
                const updatedProductForState = { ...initialData, ...finalFields };
                window.history.replaceState({ usr: updatedProductForState }, document.title);
                navigate(-1);
            } else {
                await saveProductWithRef(productRef, {
                    ...finalFields,
                    rating: 0,
                    likes_count: 0,
                    comments: []
                });
                navigate('/admin', {
                    state: {
                        newProduct: {
                            id: productId,
                            ...finalFields,
                            rating: 0,
                            likes_count: 0,
                            comments: []
                        }
                    }
                });
            }
        } catch (error) {
            console.error("Error al guardar el producto:", error);
            alert("No se pudieron guardar los cambios.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <img src={man} alt="Logo" className={styles.icon} />
                    <div className={styles.titleContainer}>
                        <h3 className={styles.title}>Dulce Como Vos</h3>
                        <span className={styles.subtitle}>
                            {isEditMode ? "Editar Producto" : "Publicar Producto"}
                        </span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <label className={styles.label}>Nombre del producto</label>
                    <input
                        name="name"
                        placeholder="Nombre del postre"
                        value={form.name}
                        onChange={handleChange}
                        className={styles.input}
                        required
                        disabled={loading}
                    />

                    <label className={styles.label}>Precio ($)</label>
                    <input
                        name="price"
                        type="text"
                        placeholder="Precio (ej: 15.000)"
                        value={displayPrice}
                        onChange={handleChange}
                        className={styles.input}
                        required
                        disabled={loading}
                    />

                    <label className={styles.label}>Categoría</label>
                    <div className={styles.categories}>
                        {availableCategories.map((category) => (
                            <CategoryButton
                                key={category.id}
                                category={category}
                                isActive={activeCategory === category.id}
                                onClick={() => {
                                    if (loading) return;
                                    setActiveCategory(category.id);
                                    setForm((prev) => ({ ...prev, category: category.id }));
                                }}
                            />
                        ))}
                    </div>

                    <label className={styles.label}>Descripción</label>
                    <textarea
                        name="description"
                        placeholder="Descripción"
                        value={form.description}
                        onChange={handleChange}
                        className={`${styles.input} ${styles.textarea}`}
                        rows="5"
                        disabled={loading}
                    />

                    <div className={styles.statusSection}>
                        <label className={styles.label}>Estado del producto</label>
                        <span className={styles.statusHelperText}>
                            Decide si este producto será visible para tus clientes
                        </span>
                        <div className={styles.statusChipsContainer}>
                            <button
                                type="button"
                                className={`${styles.statusChip} ${form.is_active ? styles.chipActive : styles.chipNeutral}`}
                                onClick={() => {
                                    if (loading) return;
                                    setForm(prev => ({ ...prev, is_active: true }));
                                }}
                                disabled={loading}
                            >
                                <span>👁 Visible para los clientes</span>
                                {form.is_active && <Check size={16} className={styles.checkIcon} />}
                            </button>
                            <button
                                type="button"
                                className={`${styles.statusChip} ${!form.is_active ? styles.chipInactive : styles.chipNeutral}`}
                                onClick={() => {
                                    if (loading) return;
                                    setForm(prev => ({ ...prev, is_active: false }));
                                }}
                                disabled={loading}
                            >
                                <span>🚫 Oculto para los clientes</span>
                                {!form.is_active && <Check size={16} className={styles.checkIcon} />}
                            </button>
                        </div>
                    </div>

                    <label className={styles.label}>
                        Fotos del producto ({selectedFiles.length}/{MAX_IMAGES})
                    </label>

                    <label className={styles.uploadBox}>
                        <div className={styles.uploadIcon}>
                            <img src={IconPlaceholder} alt="Subir fotos" />
                        </div>
                        <div className={styles.uploadInfo}>
                            <span className={styles.uploadTitle}>
                                {selectedImageIdToReplace ? 'Cambiar imagen' : 'Agregar fotos'}
                            </span>
                            <span className={styles.uploadDescription}>
                                jpg • png • webp • máx {MAX_FILE_SIZE_MB}MB c/u
                            </span>
                            {selectedFiles.length > 0 && (
                                <span className={styles.uploadSuccess}>
                                    ✓ {selectedFiles.length} imagen(es) en total
                                </span>
                            )}
                        </div>
                        <div className={styles.changeButton}>
                            {selectedImageIdToReplace ? 'Cambiar' : 'Subir'}
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            multiple={!selectedImageIdToReplace}
                            onChange={handleFileChange}
                            className={styles.hiddenInput}
                            disabled={loading || (selectedFiles.length >= MAX_IMAGES && !selectedImageIdToReplace)}
                        />
                    </label>

                    {selectedFiles.length > 0 && (
                        <>
                            <p className={styles.dragInstructions}>
                                💡 Arrastra las imágenes para ordenarlas. La primera será la portada.
                            </p>
                            <Reorder.Group
                                axis="x"
                                values={selectedFiles}
                                onReorder={setSelectedFiles}
                                className={styles.previewContainer}
                            >
                                {selectedFiles.map((item, index) => (
                                    <PreviewItem
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        selectedImageIdToReplace={selectedImageIdToReplace}
                                        onSelect={handleImageSelect}
                                        onRemove={removeFile}
                                        loading={loading}
                                    />
                                ))}
                            </Reorder.Group>
                        </>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className={styles.button}
                            style={{ backgroundColor: '#ccc', color: '#333' }}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading || selectedFiles.length === 0}
                            className={styles.button}
                        >
                            {loading ? (isEditMode ? "Guardando cambios..." : "Publicando...") : (isEditMode ? "Actualizar Producto" : "Publicar Producto")}
                        </button>
                    </div>

                    {isEditMode && (
                        <>
                            <hr />
                            <p className={styles.infoText} style={{ opacity: 0.6, fontSize: '12px' }}>ID Producto: {form.id}</p>
                        </>
                    )}
                </form>
            </div>

            {/* MODAL GLOBAL CON EDITOR DE ENCUADRE Y COMPRESIÓN */}
            <OptimizationModal
                isOpen={isModalOpen}
                file={currentFileToOptimize}
                onClose={() => {
                    setIsModalOpen(false);
                    moveToNextFile();
                }}
                onConfirm={handleConfirmOptimization}
            />
        </div>
    );
}

export default AdminProductEdit;
